import { PlusOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PORTAL_REQUEST_STATUSES, type PortalRequest, type PortalRequestLine } from "@wms/domain";
import { Alert, App, Button, Input, Modal, Space, Table, Tag } from "antd";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { portalApi } from "../api/portal";
import { errorMessage } from "../api/client";
import { formatDateTime } from "../api/format";
import { type DrawerTarget, RequestDrawer } from "./PortalRequestForm";
import { CREATE_DENIED, type RequestKind } from "./requestForm";
import { usePortalStore } from "./store";

const STATUS_COLOR: Record<string, string> = {
  draft: "default",
  submitted: "processing",
  in_review: "processing",
  accepted: "success",
  needs_clarification: "warning",
  rejected: "error",
  cancel_requested: "warning",
  cancelled: "default",
};

/** C02 / 14.3–14.4: the client's requests — a draft, «Отправить на склад», a change request after acceptance. */
export function PortalRequestsPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { me, can } = usePortalStore();
  const [params, setParams] = useSearchParams();
  // CW31-B03: the drawer knows only what to open; the card itself is read in full and gets its own form
  const [target, setTarget] = useState<DrawerTarget | null>(null);
  // CW31-B12: a direct link «?new=…» without the right to create explains itself and stays on the list
  const [createDenied, setCreateDenied] = useState(false);
  const requests = useQuery({ queryKey: ["portal", "requests"], queryFn: () => portalApi.get<PortalRequest[]>("/requests") });

  const openNew = (kind: RequestKind) => {
    if (can("portal.requests.create")) {
      setCreateDenied(false);
      setTarget({ mode: "new", kind });
    } else {
      setCreateDenied(true);
    }
  };
  useEffect(() => {
    const wanted = params.get("new");
    if ((wanted === "inbound" || wanted === "outbound") && me) {
      // the home-page buttons open the same drawer as the buttons here: the warehouse and the kind are prefilled
      openNew(wanted);
      params.delete("new");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, setParams, me]);
  // ТЗ «Рабочие столы» 31.10.2026 (UXW-27): «Требует внимания» opens exactly this request — the same drawer as its
  // number in the list (a request of another client answers «не найдена» there, as in the list)
  useEffect(() => {
    const wanted = params.get("open");
    if (!wanted) return;
    setTarget({ mode: "open", id: wanted });
    params.delete("open");
    setParams(params, { replace: true });
  }, [params, setParams]);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["portal", "requests"] });
  const submit = useMutation({
    mutationFn: (id: string) => portalApi.post<PortalRequest>(`/requests/${id}/submit`),
    onSuccess: (saved) => {
      message.success(`Заявка ${saved.number} отправлена на склад. Состояние: ${PORTAL_REQUEST_STATUSES[saved.status]}`);
      refresh();
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => portalApi.post<PortalRequest>(`/requests/${id}/cancel-request`, { reason }),
    onSuccess: (saved) => {
      message.success(saved.status === "cancelled" ? "Заявка отменена" : "Запрос отмены передан складу: факт исполнения не отменяется сам по себе");
      // the list and an opened card are read again: «Отменена» / «Запрошена отмена» everywhere at once
      refresh();
    },
    onError: (e) => message.error(errorMessage(e)),
  });
  const askCancel = (request: PortalRequest) => {
    let reason = "";
    Modal.confirm({
      title: `Отменить заявку ${request.number}?`,
      content: <Input.TextArea placeholder="Причина" onChange={(e) => (reason = e.target.value)} rows={3} />,
      okText: "Запросить отмену",
      cancelText: "Вернуться",
      onOk: () => cancel.mutateAsync({ id: request.id, reason: reason || "по просьбе клиента" }).then(() => undefined),
    });
  };

  const columns = [
    {
      title: "Номер",
      dataIndex: "number",
      render: (v: string, r: PortalRequest) => (
        <a onClick={() => setTarget({ mode: "open", id: r.id })} data-testid={`portal-open-${v}`}>
          {v}
        </a>
      ),
    },
    { title: "Тип", dataIndex: "kind", render: (v: string) => (v === "inbound" ? "Поступление" : "Отгрузка") },
    { title: "Создана", dataIndex: "created_at", render: (v: string) => formatDateTime(v) },
    { title: "Состояние", dataIndex: "status", render: (v: string) => <Tag color={STATUS_COLOR[v]}>{PORTAL_REQUEST_STATUSES[v] ?? v}</Tag> },
    { title: "Документ склада", dataIndex: "document_number", render: (v: string | null) => v ?? "" },
    { title: "Ответ склада", dataIndex: "operator_comment", render: (v: string | null) => v ?? "" },
    {
      title: "",
      key: "actions",
      render: (_: unknown, r: PortalRequest) => (
        <Space wrap>
          {["draft", "needs_clarification"].includes(r.status) && can("portal.requests.submit") && (
            <Button size="small" type="primary" onClick={() => submit.mutate(r.id)} data-testid={`portal-submit-${r.number}`}>
              Отправить на склад
            </Button>
          )}
          {!["cancelled", "rejected", "cancel_requested"].includes(r.status) && can("portal.requests.cancel_request") && (
            <Button size="small" onClick={() => askCancel(r)}>
              Отменить
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div className="wms-page-header">
        <div>
          <h1>Заявки</h1>
          <div className="wms-subtitle">Заявка — просьба складу; остаток меняется только после подтверждённой приёмки или отгрузки</div>
        </div>
        {can("portal.requests.create") && (
          <Space wrap>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openNew("inbound")} data-testid="portal-request-inbound">
              Сообщить о поступлении
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openNew("outbound")} data-testid="portal-request-outbound">
              Заказать отгрузку
            </Button>
          </Space>
        )}
      </div>
      {createDenied && (
        <Alert type="info" showIcon closable onClose={() => setCreateDenied(false)} style={{ marginBottom: 12 }} message={CREATE_DENIED} data-testid="portal-request-create-denied" />
      )}
      {requests.isError && <Alert type="error" showIcon message={errorMessage(requests.error)} style={{ marginBottom: 12 }} />}
      <Table<PortalRequest> rowKey="id" size="middle" loading={requests.isLoading} dataSource={requests.data ?? []} columns={columns} pagination={{ pageSize: 25 }} scroll={{ x: true }} locale={{ emptyText: "Заявок пока нет" }} />
      <RequestDrawer target={target} onClose={() => setTarget(null)} />
    </div>
  );
}

export type { PortalRequestLine };
